import React, { useEffect, useState } from "react";
import { usePremisesStore } from "../../../store/store";
import { ArrowLeft } from "lucide-react";
import { useNavigate, useParams, useLocation, Link } from "react-router-dom";
import { handleApiError } from "../../../common/utils";
import axiosInstance from "../../../common/axiosInstance";
import { PageLoader } from "../../../components/loader/Loader";
import {
  useChecklistCategories,
  ChecklistCategory,
} from "./WashroomChecklistCategory/useChecklistCategories"; // Import the hook
// import { WASHROOM_TYPES } from "../../../constants/modules";
import { v4 as uuidv4 } from "uuid";
import { getAllTypeList } from "../../../service/api";
import { WashroomTypeListType } from "../../../types/api";

interface ChecklistItem {
  id?: string;
  question: string;
  type: string;
  weightage: number;
  categoryId: string;
}

interface Checklist {
  _id: string;
  name: string;
  premisesId: string;
  washroomType: string;
  items: ChecklistItem[];
  createdAt: string;
  updatedAt: string;
}

const CATEGORY_OPTIONS = [
  { value: "1", label: "Hygiene" },
  { value: "2", label: "Accessibility" },
  { value: "3", label: "Odour" },
  { value: "4", label: "Safety" },
  { value: "5", label: "Comfort" },
  { value: "6", label: "Environment" },
  { value: "7", label: "Fixtures" },
  { value: "8", label: "Consumables" },
  { value: "9", label: "Other" },
];
const TYPE_OPTIONS = [
  { value: "yesno", label: "Yes/No" },
  { value: "rating", label: "Rating (1-5)" },
];

interface ChecklistItemRowProps {
  item: ChecklistItem;
  idx: number;
  onChange: (idx: number, field: keyof ChecklistItem, value: any) => void;
  onRemove: (idx: number) => void;
  isViewMode?: boolean;
  categories: ChecklistCategory[];
  categoriesLoading: boolean;
}

const ChecklistItemRow: React.FC<ChecklistItemRowProps> = ({
  item,
  idx,
  onChange,
  onRemove,
  isViewMode = false,
  categories,
  categoriesLoading,
}) => {
  // Find category name for display in view mode
  const getCategoryName = (categoryId: string) => {
    const category = categories.find((cat) => cat.categoryId === categoryId);
    return category ? category.categoryName : categoryId;
  };

  return (
    <div className="grid grid-cols-12 gap-4 px-4 py-3 items-start border-t bg-gray-50 mb-2 rounded-md">
      <div className="col-span-3 flex flex-col justify-center">
        {isViewMode ? (
          <input
            type="text"
            className="border rounded px-2 py-2 w-full bg-gray-100"
            value={getCategoryName(item.categoryId)}
            disabled
          />
        ) : (
          <select
            className="border rounded px-2 pr-8 py-2 w-full focus:outline-none focus:ring-0 appearance-none bg-white"
            value={item.categoryId}
            onChange={(e) => onChange(idx, "categoryId", e.target.value)}
            required
            disabled={categoriesLoading}
            style={{
              backgroundImage:
                "url(\"data:image/svg+xml,%3Csvg width='16' height='16' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M4 6l4 4 4-4' stroke='%2361748e' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E\")",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "right 0.75rem center",
              backgroundSize: "1.25em 1.25em",
            }}
          >
            <option value="">
              {categoriesLoading ? "Loading categories..." : "Select Category"}
            </option>
            {categories.map((category) => (
              <option key={category._id} value={category.categoryId}>
                {category.categoryName}
              </option>
            ))}
          </select>
        )}
      </div>
      <div className="col-span-5 flex flex-col justify-center">
        <input
          type="text"
          className="border rounded px-2 py-2 w-full focus:outline-none focus:ring-0"
          placeholder="Enter question"
          value={item.question || ""}
          onChange={(e) => onChange(idx, "question", e.target.value)}
          required
          disabled={isViewMode}
        />
      </div>
      <div className="col-span-2 flex flex-col justify-center">
        <select
          className="border rounded px-2 pr-8 py-2 w-full focus:outline-none focus:ring-0 appearance-none bg-white"
          value={item.type}
          onChange={(e) => onChange(idx, "type", e.target.value)}
          required
          disabled={isViewMode}
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml,%3Csvg width='16' height='16' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M4 6l4 4 4-4' stroke='%2361748e' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E\")",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "right 0.75rem center",
            backgroundSize: "1.25em 1.25em",
          }}
        >
          <option value="">Select Type</option>
          {TYPE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>
      <div className="col-span-1 flex flex-col justify-center">
        <input
          type="number"
          className="border rounded px-2 py-2 w-full focus:outline-none focus:ring-0 text-center"
          placeholder="Weight %"
          value={item.weightage}
          min={0}
          max={100}
          onChange={(e) => onChange(idx, "weightage", e.target.value)}
          required
          disabled={isViewMode}
        />
      </div>
      {!isViewMode && (
        <div className="col-span-1 flex items-center justify-end h-full">
          <button
            type="button"
            className="text-red-500 hover:underline font-semibold text-sm px-2 py-1 rounded transition-colors duration-150 focus:outline-none focus:ring-0 border-0 shadow-none"
            onClick={() => onRemove(idx)}
            aria-label="Delete item"
            style={{ boxShadow: "none", border: "none" }}
          >
            Delete
          </button>
        </div>
      )}
    </div>
  );
};

const ChecklistConfigForm: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { id: urlId } = useParams();

  const mode = location.pathname.includes("/view/")
    ? "view"
    : location.pathname.includes("/edit/")
      ? "edit"
      : "create";

  const isViewMode = mode === "view";
  const isEditMode = mode === "edit";

  const {
    categories,
    loading: categoriesLoading,
    error: categoriesError,
    refreshCategories,
    clearError: clearCategoriesError,
  } = useChecklistCategories({
    autoFetch: true,
    pollingInterval: 30000,
    refreshOnFocus: true,
  });

  const [washroomTypes, setWashroomTypes] = useState<WashroomTypeListType[]>(
    [],
  );
  const [checklistItems, setChecklistItems] = useState<ChecklistItem[]>([
    { question: "", type: "", weightage: 0, categoryId: "", id: uuidv4() },
  ]);
  const [selectedPremises, setSelectedPremises] = useState("");
  const [selectedWashroomType, setSelectedWashroomType] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [name, setName] = useState("");
  const { allPremises, fetchPremises } = usePremisesStore();

  useEffect(() => {
    if (allPremises.length === 0) fetchPremises();
  }, []);

  useEffect(() => {
    if (urlId && (isViewMode || isEditMode)) {
      setLoading(true);
      axiosInstance
        .get(`/api/washroom-checklist-config/${urlId}`)
        .then((response) => {
          if (response.status === 200) {
            const data = response.data.data;
            setChecklistItems(data.items || []);
            setSelectedPremises(data.premisesId);
            setSelectedWashroomType(data.washroomType);
            setName(data.name);
          }
        })
        .catch((error) => {
          setError("Failed to load checklist data");
          console.error("Error loading checklist:", error);
          if (error.response?.status === 404) {
            navigate("/washroom-management/checklists");
          }
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [urlId, mode, allPremises.length, fetchPremises, navigate]);

  useEffect(() => {
    if (!categoriesError) {
      clearCategoriesError();
    }
  }, [categoriesError, clearCategoriesError]);

  const fetchTypes = async () => {
    try {
      setLoading(false);
      const res = await getAllTypeList();

      if (res.data?.data && Array.isArray(res.data.data)) {
        setWashroomTypes(res.data.data);
      } else {
        setWashroomTypes([]);
      }
    } catch (error) {
      console.error("Failed to fetch washroom types: ", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTypes();
  }, []);

  const handleAddItem = () =>
    setChecklistItems((prev) => [
      ...prev,
      { question: "", type: "", weightage: 0, categoryId: "", id: uuidv4() },
    ]);

  const handleRemoveItem = (idx: number) =>
    setChecklistItems((prev) => prev.filter((_, i) => i !== idx));

  const handleItemChange: ChecklistItemRowProps["onChange"] = (
    idx,
    field,
    value,
  ) => {
    setChecklistItems((prev) => {
      const updated = [...prev];
      if (field === "weightage") {
        updated[idx].weightage = Number(value);
      } else {
        updated[idx][field] = value;
      }
      return updated;
    });
  };

  const totalWeight = checklistItems.reduce(
    (sum, item) => sum + Number(item.weightage),
    0,
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isViewMode) return;

    setError("");
    setSuccess("");
    if (!selectedPremises || !selectedWashroomType)
      return setError("Please select premises and washroom type.");
    if (checklistItems.length === 0)
      return setError("Add at least one checklist item.");
    if (totalWeight !== 100) return setError("Total weightage must be 100%.");

    // Validate that all categories are selected
    const invalidItems = checklistItems.filter((item) => !item.categoryId);
    if (invalidItems.length > 0) {
      return setError("Please select a category for all checklist items.");
    }

    setLoading(true);
    const body = {
      name,
      premisesId: selectedPremises,
      washroomType: selectedWashroomType,
      items: checklistItems.map(
        ({ question, type, weightage, categoryId, id }) => ({
          question,
          type,
          weightage,
          categoryId,
          id: id || uuidv4(),
        }),
      ),
    };

    try {
      const url = isEditMode
        ? `/api/washroom-checklist-config/${urlId}`
        : `/api/washroom-checklist-config`;

      const response = await axiosInstance({
        method: isEditMode ? "put" : "post",
        url,
        data: body,
      });

      if (response.status === 200 || response.status === 201) {
        setSuccess("Checklist saved successfully!");
        if (!isEditMode) {
          setChecklistItems([]);
          setSelectedPremises("");
          setSelectedWashroomType("");
          setName("");
        }
        setTimeout(() => {
          navigate("/washroom-management/checklists");
        }, 1500);
      } else {
        setError(response.data.error || "Failed to save checklist.");
      }
    } catch (error: any) {
      setError(error.response?.data?.error || "Failed to save checklist.");
      console.error("Error saving checklist:", error);
    } finally {
      setLoading(false);
    }
  };

  // Manual refresh button for categories
  const handleRefreshCategories = () => {
    refreshCategories();
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <PageLoader />
      </div>
    );
  }

  return (
    <>
      <div className="px-2 py-4 mt-14 min-h-100vh">
        <div className="flex justify-between items-center mb-3">
          <h2 className="mb-3">
            {isViewMode
              ? "View Checklist"
              : isEditMode
                ? "Edit Checklist"
                : "Create Checklist"}
          </h2>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="rounded-full flex justify-center items-center px-4 py-2 text-lg font-semibold bg-[#61748e] text-white"
              onClick={() => navigate("/washroom-management/checklists")}
            >
              <ArrowLeft className="mr-2 h-4 w-4" /> BACK
            </button>
          </div>
        </div>
        <form
          className="bg-white rounded-t-lg shadow-md mt-2 p-8 w-full"
          id="checklist-form"
          onSubmit={handleSubmit}
        >
          {error && <div className="text-red-500 mb-2">{error}</div>}
          {success && <div className="text-green-600 mb-2">{success}</div>}
          {categoriesError && (
            <div className="text-orange-500 mb-2">
              Warning: Failed to load categories. Please refresh or contact
              support.
            </div>
          )}

          <div className="flex gap-4 mb-6">
            <div className="flex-1">
              <label className="block text-sm font-medium mb-1">
                Checklist Name
              </label>
              <input
                type="text"
                name="name"
                className="border rounded px-3 py-2 w-full"
                placeholder="Enter checklist name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={isViewMode}
                required
              />
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium mb-1">Premises</label>
              <select
                className="border rounded px-3 py-2 w-full"
                value={selectedPremises}
                onChange={(e) => setSelectedPremises(e.target.value)}
                disabled={isViewMode}
                required
              >
                <option value="">Select Premises</option>
                {allPremises.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.premisesName}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium mb-1">
                Washroom Type
              </label>
              <select
                className="border rounded px-3 py-2 w-full"
                value={selectedWashroomType}
                onChange={(e) => setSelectedWashroomType(e.target.value)}
                disabled={isViewMode}
                required
              >
                <option value="">Select Washroom Type</option>
                {washroomTypes.map((type) => (
                  <option key={type._id} value={type._id}>
                    {type.typeName}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="mb-6">
            <div className="flex justify-between items-center mb-2">
              <label className="block text-sm font-medium">
                Checklist Items
              </label>
              {!isViewMode && (
                <button
                  type="button"
                  onClick={handleRefreshCategories}
                  className="text-sm text-[#0fa9a6] hover:underline flex items-center gap-1"
                  disabled={categoriesLoading}
                >
                  {categoriesLoading ? "Refreshing..." : "Refresh Categories"}
                </button>
              )}
            </div>
            <div className="rounded">
              <div className="grid grid-cols-12 gap-4 bg-gray-100 px-4 py-2 font-semibold text-sm">
                <div className="col-span-3">
                  Category{" "}
                  {categoriesLoading && (
                    <span className="text-xs">(Loading...)</span>
                  )}
                </div>
                <div className="col-span-5">Question</div>
                <div className="col-span-2">Type</div>
                <div className="col-span-1 text-center">Weightage</div>
                {!isViewMode && <div className="col-span-1"></div>}
              </div>
              {checklistItems.map((item, idx) => (
                <ChecklistItemRow
                  key={idx}
                  item={item}
                  idx={idx}
                  onChange={handleItemChange}
                  onRemove={handleRemoveItem}
                  isViewMode={isViewMode}
                  categories={categories}
                  categoriesLoading={categoriesLoading}
                />
              ))}
            </div>
            {!isViewMode && (
              <button
                type="button"
                className="mt-3 text-[#00A8A5] bg-[#EAFBFA] px-4 py-1 rounded font-semibold border border-[#00A8A5] transition-colors duration-150 hover:bg-[#00A8A5] hover:text-white focus:outline-none focus:ring-0"
                onClick={handleAddItem}
              >
                Add More
              </button>
            )}
            <div className="mt-2 text-sm text-gray-600">
              Total Weightage: {totalWeight}%
            </div>
          </div>
        </form>
        <div className="flex rounded-md justify-end gap-4 p-4 bg-[#5A7192]  sticky bottom-0">
          {!isViewMode && (
            <button
              type="submit"
              form="checklist-form"
              className="px-6 py-2 rounded-full bg-[#00A8A8] text-white hover:bg-teal-600 transition"
              disabled={loading || categoriesLoading}
            >
              {loading ? "Saving..." : "Save Checklist"}
            </button>
          )}
          {isViewMode && (
            <Link
              to="/washroom-management/checklists"
              className="px-6 py-2 rounded-full bg-[#00A8A8] text-white hover:bg-teal-600 transition"
            >
              Back To Checklists
            </Link>
          )}
        </div>
      </div>
    </>
  );
};

export default ChecklistConfigForm;
